<template>
  <div>
    <v-snackbar
      v-model="dialog.snackbar"
      top
      right
      color="green">
      <span class="body-1 font-weight-light white--text"
        v-html="message.success">
      </span>

      <template v-slot:action="{ attrs }">
        <v-btn
          class="body-1 font-weight-bold text-capitalize"
          color="white"
          icon
          v-bind="attrs"
          @click="dialog.snackbar = false">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="dialog.filter"
      @keydown.esc="dialog.filter = false"
      @keydown.enter="dialog.filter = false; dialog.fullscreen = false; fetch();"
      :fullscreen="dialog.fullscreen"
      width="550"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Filter Data

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.filter = false; dialog.fullscreen = false;"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="py-0">
          <v-form>
            <v-row v-for="(item, index) in filters" :key="index">
              <!-- NON COLUMN -->
              <v-col v-if="item.type !== 'column'">
                <div v-if="item.type === 'text' || item.type === 'number'">
                  <p class="body-2 mb-2">
                    {{ item.label }}
                  </p>

                  <v-text-field
                    :style="`max-width: ${item.width};`"
                    filled
                    rounded
                    dense
                    :color="set_color"
                    hide-details
                    :type="item.type"
                    :placeholder="item.placeholder"
                    clearable
                    @click:clear="item.value = ''"
                    v-model="item.value"
                    vvvv-uppercase
                    style="border-radius: 10px !important;">
                  </v-text-field>
                </div>

                <div v-if="item.type === 'select'">
                  <p class="body-2 mb-2">
                    {{ item.label }}
                  </p>

                  <v-select
                    :style="`max-width: ${item.width};`"
                    filled
                    rounded
                    dense
                    single-line
                    hide-details
                    :color="set_color"
                    :item-color="set_color"
                    :placeholder="item.placeholder"
                    :items="item.option"
                    clearable
                    @click:clear="item.value = ''"
                    v-model="item.value"
                    style="border-radius: 10px !important;">
                  </v-select>
                </div>

                <div v-if="item.type === 'radio'">
                  <p class="body-2 mb-2">
                    {{ item.label }}
                  </p>

                  <v-radio-group
                    class="mt-0"
                    v-model="item.value"
                    row
                    hide-details>
                    <v-radio v-for="(radio, i) in item.option" :key="i"
                      :label="radio"
                      :color="set_color"
                      :value="radio">
                    </v-radio>
                  </v-radio-group>
                </div>

                <div v-if="item.type === 'date'">
                  <p class="body-2 mb-2">
                    {{ item.label }}
                  </p>

                  <v-menu
                    :ref="`date${index}`"
                    min-width="290px"
                    max-width="290px"
                    transition="scale-transition"
                    offset-y
                    :nudge-right="40"
                    :close-on-content-click="false"
                    v-model="item.picker">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :style="`max-width: ${item.width};`"
                        v-model="item.value"
                        vvvv-uppercase
                        readonly
                        filled
                        rounded
                        dense
                        :color="set_color"
                        :placeholder="item.placeholder"
                        hide-details
                        clearable
                        @click:clear="item.value = ''"
                        v-on="on"
                        v-bind="attrs"
                        style="border-radius: 10px !important;">
                      </v-text-field>
                    </template>

                    <v-date-picker 
                      :color="set_color"
                      v-model="item.value"
                      @input="item.picker = false">
                    </v-date-picker>
                  </v-menu>
                </div>
              </v-col>

              <!-- WITH COLUMN -->
              <v-col v-else
                v-for="(subItem, key) in item.sub" :key="key">
                <div v-if="subItem.type === 'text' || subItem.type === 'number'">
                  <p class="body-2 mb-2">
                    {{ subItem.label }}
                  </p>

                  <v-text-field
                    :style="`max-width: ${item.width};`"
                    filled
                    rounded
                    dense
                    :color="set_color"
                    hide-details
                    :type="subItem.type"
                    :placeholder="subItem.placeholder"
                    clearable
                    @click:clear="subItem.value = ''"
                    v-model="subItem.value"
                    vvvv-uppercase
                    style="border-radius: 10px !important;">
                  </v-text-field>
                </div>

                <div v-if="subItem.type === 'select'">
                  <p class="body-2 mb-2">
                    {{ subItem.label }}
                  </p>

                  <v-select
                    :style="`max-width: ${item.width};`"
                    filled
                    rounded
                    dense
                    single-line
                    hide-details
                    :color="set_color"
                    :item-color="set_color"
                    :placeholder="subItem.placeholder"
                    :items="subItem.option"
                    clearable
                    @click:clear="subItem.value = ''"
                    v-model="subItem.value"
                    style="border-radius: 10px !important;">
                  </v-select>
                </div>

                <div v-if="subItem.type === 'radio'">
                  <p class="body-2 mb-2">
                    {{ subItem.label }}
                  </p>

                  <v-radio-group
                    class="mt-0"
                    v-model="subItem.value"
                    row
                    hide-details>
                    <v-radio v-for="(radio, i) in subItem.option" :key="i"
                      :label="radio"
                      :color="set_color"
                      :value="radio">
                    </v-radio>
                  </v-radio-group>
                </div>

                <div v-if="subItem.type === 'date'">
                  <p class="body-2 mb-2">
                    {{ subItem.label }}
                  </p>

                  <v-menu
                    :ref="`subdate${key}`"
                    min-width="290px"
                    max-width="290px"
                    transition="scale-transition"
                    offset-y
                    :nudge-right="40"
                    :close-on-content-click="false"
                    v-model="subItem.picker">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :style="`max-width: ${item.width};`"
                        v-model="subItem.value"
                        vvvv-uppercase
                        readonly
                        filled
                        rounded
                        dense
                        :color="set_color"
                        :placeholder="subItem.placeholder"
                        hide-details
                        clearable
                        @click:clear="subItem.value = ''"
                        v-on="on"
                        v-bind="attrs"
                        style="border-radius: 10px !important;">
                      </v-text-field>
                    </template>

                    <v-date-picker 
                      :color="set_color"
                      v-model="subItem.value"
                      @input="subItem.picker = false">
                    </v-date-picker>
                  </v-menu>
                </div>
              </v-col>
            </v-row>

            <v-row v-if="config.action.filter.location !== undefined && config.action.filter.location">
              <v-col
                cols="12"
                md="6">
                <div class="body-2 mb-2">
                  Kecamatan
                </div>

                <v-autocomplete
                  filled
                  rounded
                  dense
                  :color="set_color"
                  hide-details
                  placeholder="Contoh. Trucuk"

                  :search-input.sync="autocomplete.subdistrict"
                  
                  :items="static_loc.subdistrict"
                  item-value="kecamatan_code"
                  item-text="kecamatan_name"
                  :item-color="set_color"

                  v-model="form_static.subdistrict"
                  style="border-radius: 10px !important;">
                </v-autocomplete>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <div class="body-2 mb-2">
                  Kelurahan
                </div>

                <v-autocomplete
                  filled
                  rounded
                  dense
                  :color="set_color"
                  hide-details
                  placeholder="Contoh. Kradenan"

                  :search-input.sync="autocomplete.village"
                  
                  :items="static_loc.village"
                  item-value="desa_code"
                  item-text="desa_name"
                  :item-color="set_color"

                  v-model="form_static.village"
                  style="border-radius: 10px !important;">
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="mx-4 my-3"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            :color="set_color"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            @click="dialog.filter = false; dialog.fullscreen = false; fetch();">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Filter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.freeze"
      @keydown.esc="dialog.freeze = false"
      :fullscreen="dialog.fullscreen"
      width="400"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Freeze Kolom

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.freeze = false; dialog.fullscreen = false; addFreezes();"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="py-0">
          <v-form>
            <v-row>
              <v-col>
                <div>
                  <p class="body-2 mb-2">
                    Silahkan pilih kolom yang akan di <b class="font-italic">"Freeze"</b>
                  </p>

                  <v-list-item-group
                    multiple>
                    <v-list flat>
                      <v-list-item 
                        v-for="(item, index) in configHeaders" 
                        :key="index"
                        class="px-0"
                        @click="item.freeze = !item.freeze ? true : false">
                        <v-list-item-action class="mr-4">
                          <v-checkbox
                            :color="set_color"
                            :input-value="item.freeze">
                          </v-checkbox>
                        </v-list-item-action>

                        <v-list-item-content>
                          <p
                            class="mb-0"
                            :class="item.freeze ? 'font-weight-bold' : ''"
                            :style="item.freeze ? `color: ${set_color};` : ''">
                            {{ item.text }}
                          </p>
                        </v-list-item-content>
                      </v-list-item>  
                    </v-list>
                  </v-list-item-group>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            :color="set_color"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            @click="dialog.freeze = false; dialog.fullscreen = false; addFreezes();">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Freeze
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.hide"
      @keydown.esc="dialog.hide = false"
      :fullscreen="dialog.fullscreen"
      width="400"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Hide Kolom

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.hide = false; dialog.fullscreen = false;"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="py-0">
          <v-form>
            <v-row>
              <v-col>
                <div>
                  <p class="body-2 mb-2">
                    Silahkan pilih kolom yang akan di <b class="font-italic">"Hide"</b>
                  </p>

                  <v-list-item-group
                    multiple>
                    <v-list flat>
                      <v-list-item 
                        v-for="(item, index) in headers" 
                        :key="index"
                        class="px-0"
                        @click="item.hide = !item.hide ? true : false">
                        <v-list-item-action class="mr-4">
                          <v-checkbox
                            :color="set_color"
                            :input-value="item.hide">
                          </v-checkbox>
                        </v-list-item-action>

                        <v-list-item-content>
                          <p
                            class="mb-0"
                            :class="item.hide ? 'font-weight-bold' : ''"
                            :style="item.hide ? `color: ${set_color};` : ''">
                            {{ item.text }}
                          </p>
                        </v-list-item-content>
                      </v-list-item>  
                    </v-list>
                  </v-list-item-group>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            :color="set_color"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            @click="dialog.hide = false; dialog.fullscreen = false; addHides();">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Hide
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.delete"
      @keydown.esc="dialog.delete = false"
      :fullscreen="dialog.fullscreen"
      width="500"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Hapus Data

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.delete = false; dialog.fullscreen = false; selected.delete = [];"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="body-1 font-weight-light text-first mt-3">
          Apakah Anda yakin menghapus data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b> yang Anda pilih?
        </v-card-text>

        <v-alert v-if="message.error.length > 0"
          class="mx-6"
          type="error" 
          dense
          text 
          prominent>
          {{ message.error }}
        </v-alert>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            color="green"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            :disabled="process.delete"
            :loading="process.delete"
            @click="deletes()">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Ya, Hapus
          </v-btn>

          <v-btn
            outlined
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize red--text"
            :disabled="process.delete"
            @click="dialog.delete = false; dialog.fullscreen = false; selected.delete = [];">
            <v-icon color="red" small class="mr-1">mdi-close-circle</v-icon>
            Batal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.approve"
      @keydown.esc="dialog.approve = false"
      :fullscreen="dialog.fullscreen"
      width="500"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Setujui Data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b>

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.approve = false; dialog.fullscreen = false; selected.approve = [];"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="body-1 font-weight-light text-first mt-3">
          Apakah Anda yakin menyetujui data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b> yang Anda pilih?
        </v-card-text>

        <v-alert v-if="message.error.length > 0"
          class="mx-6"
          type="error" 
          dense
          text 
          prominent>
          {{ message.error }}
        </v-alert>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            color="green"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            :disabled="process.approve"
            :loading="process.approve"
            @click="approved()">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Ya, Setujui
          </v-btn>

          <v-btn
            outlined
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize red--text"
            :disabled="process.approve"
            @click="dialog.approve = false; dialog.fullscreen = false; selected.approve = [];">
            <v-icon color="red" small class="mr-1">mdi-close-circle</v-icon>
            Batal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.reject"
      @keydown.esc="dialog.reject = false"
      :fullscreen="dialog.fullscreen"
      width="500"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Tolak Data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b>

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.reject = false; dialog.fullscreen = false; selected.reject = [];"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="body-1 font-weight-light text-first mt-3">
          <div class="mb-3">
            Apakah Anda yakin menolak data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b> yang Anda pilih? Jika iya silahkan isi catatan dibawah ini :
          </div>

          <div>
            <v-textarea
              outlined
              dense
              rows="3"
              hide-details
              :color="set_color"
              placeholder="Contoh. Tidak masuk dalam kategori"
              v-model="form.reason"
              vvvv-uppercase>
            </v-textarea>
          </div>
        </v-card-text>

        <v-alert v-if="message.error.length > 0"
          class="mx-6"
          type="error" 
          dense
          text 
          prominent>
          {{ message.error }}
        </v-alert>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            color="green"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            :disabled="process.reject"
            :loading="process.reject"
            @click="rejected()">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Ya, Tolak
          </v-btn>

          <v-btn
            outlined
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize red--text"
            :disabled="process.reject"
            @click="dialog.reject = false; dialog.fullscreen = false; selected.reject = [];">
            <v-icon color="red" small class="mr-1">mdi-close-circle</v-icon>
            Batal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.active"
      @keydown.esc="dialog.active = false"
      :fullscreen="dialog.fullscreen"
      width="500"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Aktif Data

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.active = false; dialog.fullscreen = false; selected.delete = [];"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="body-1 font-weight-light text-first mt-3">
          Apakah Anda yakin mengaktifkan data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b> yang Anda pilih?
        </v-card-text>

        <v-alert v-if="message.error.length > 0"
          class="mx-6"
          type="error" 
          dense
          text 
          prominent>
          {{ message.error }}
        </v-alert>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            color="green"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            :disabled="process.active"
            :loading="process.active"
            @click="actived()">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Ya, Aktif
          </v-btn>

          <v-btn
            outlined
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize red--text"
            :disabled="process.active"
            @click="dialog.active = false; dialog.fullscreen = false; selected.active = [];">
            <v-icon color="red" small class="mr-1">mdi-close-circle</v-icon>
            Batal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.deactive"
      @keydown.esc="dialog.deactive = false"
      :fullscreen="dialog.fullscreen"
      width="500"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Nonaktif Data

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.deactive = false; dialog.fullscreen = false; selected.delete = [];"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="body-1 font-weight-light text-first mt-3">
          Apakah Anda yakin menonaktifkan data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b> yang Anda pilih?
        </v-card-text>

        <v-alert v-if="message.error.length > 0"
          class="mx-6"
          type="error" 
          dense
          text 
          prominent>
          {{ message.error }}
        </v-alert>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            color="green"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            :disabled="process.deactive"
            :loading="process.deactive"
            @click="deactived()">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Ya, Nonaktif
          </v-btn>

          <v-btn
            outlined
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize red--text"
            :disabled="process.deactive"
            @click="dialog.deactive = false; dialog.fullscreen = false; selected.deactive = [];">
            <v-icon color="red" small class="mr-1">mdi-close-circle</v-icon>
            Batal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.attachment"
      @keydown.esc="dialog.attachment = false"
      :fullscreen="dialog.fullscreen"
      width="1000"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Lampiran

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.attachment = false; dialog.fullscreen = false; selected.row = [];"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text 
          class="body-1 font-weight-light text-first mt-3">
          <v-img v-if="selected.row.length > 0"
            width="100%"
            height="auto"
            alt="amanata" 
            :src="selected.row[0].url"
            class="ma-auto"
            style="border-radius: 15px;">
            <template v-slot:placeholder>
              <v-row
                class="fill-height"
                align="center"
                justify="center">
                <v-progress-circular 
                  indeterminate 
                  color="white">
                </v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </v-card-text>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            outlined
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize red--text"
            :disabled="process.attachment"
            @click="dialog.attachment = false; dialog.fullscreen = false; selected.row = [];">
            <v-icon color="red" small class="mr-1">mdi-close-circle</v-icon>
            Tutup
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CALL FORM  -->
    <v-dialog v-model="dialog.form"
      @keydown.esc="dialog.form = false"
      :fullscreen="dialog.fullscreen"
      width="625"
      persistent
      scrollable>
      <forms v-if="dialog.form"
        :config="config" 
        :detail="detail" 
        :fullscreen="dialog.fullscreen" 
        @screen="dialog.fullscreen = $event"
        @snackbar="message.success = $event;"
        @close="dialog.form = $event; dialog.fullscreen = false; fetch();" />
    </v-dialog>

    <!-- CALL FORM DOC USER  -->
    <v-dialog v-model="dialog.form_user_doc"
      @keydown.esc="dialog.form_user_doc = false"
      :fullscreen="dialog.fullscreen"
      width="625"
      persistent
      scrollable>
      <formdocs 
        :form="form_user_doc" 
        :fullscreen="dialog.fullscreen" 
        @screen="dialog.fullscreen = $event"
        @snackbar="message.success = $event; dialog.snackbar = true;"
        @close="dialog.form_user_doc = $event; dialog.fullscreen = false; dialog.snackbar = false;" />
    </v-dialog>

    <v-dialog v-model="dialog.reset_password"
      @keydown.esc="dialog.reset_password = false"
      :fullscreen="dialog.fullscreen"
      width="500"
      persistent
      scrollable>
      <v-card 
        :class="dialog.fullscreen ? 'non-border-radius' : 'border-radius'"
        class="pa-2">
        <v-card-title class="d-flex align-center py-0 px-3 text-right popup-header">
          Reset Password

          <v-spacer/>

          <v-icon 
            @click="dialog.fullscreen = !dialog.fullscreen ? true : false"
            class="cursor-pointer mr-1"
            size="24"
            :color="set_color">
            {{ !dialog.fullscreen ? 'mdi-fullscreen' : 'mdi-fullscreen-exit' }}
          </v-icon>

          <v-icon 
            @click="dialog.reset_password = false; dialog.fullscreen = false; selected.reset_password = [];"
            class="cursor-pointer"
            size="24"
            color="red">
            mdi-close-circle-outline
          </v-icon>
        </v-card-title>
        
        <v-divider class="mx-4 mt-3 mb-2"/>

        <v-card-text class="body-1 font-weight-light text-first mt-3">
          Apakah Anda yakin mereset password data <b class="font-weight-bold black--text">{{ $route.meta.title }}</b> yang Anda pilih?
        </v-card-text>

        <v-alert v-if="message.error.length > 0"
          class="mx-6"
          type="error" 
          dense
          text 
          prominent>
          {{ message.error }}
        </v-alert>

        <v-divider class="mx-4 mb-3 mt-0"/>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            color="green"
            elevation="0"
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize white--text"
            :disabled="process.reset_password"
            :loading="process.reset_password"
            @click="resetPassword()">
            <v-icon small class="mr-1">mdi-check-bold</v-icon>
            Ya, Reset
          </v-btn>

          <v-btn
            outlined
            style="border-radius: 10px;"
            class="body-2 font-weight-bold text-capitalize red--text"
            :disabled="process.reset_password"
            @click="dialog.reset_password = false; dialog.fullscreen = false; selected.reset_password = [];">
            <v-icon color="red" small class="mr-1">mdi-close-circle</v-icon>
            Batal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row>
      <v-col 
        cols="12"
        :class="config.padding">
        <v-card 
          flat 
          style="border: 1px solid #E0E0E0;">
          <v-card-text :class="config.action.tab.button ? 'pb-0' : ''">
            <v-row>
              <v-col 
                cols="12" 
                md="8" 
                class="d-flex align-center">
                <!-- FILTER -->
                <div class="mr-3" v-if="config.action.filter.button">
                  <v-btn
                    elevation="0"
                    dark
                    style="border-radius: 10px;"
                    :color="set_color"
                    class="body-2 text-capitalize font-weight-bold d-flex align-center"
                    @click="dialog.filter = true">
                    <v-icon size="18">mdi-filter-outline</v-icon>

                    <span class="ml-1">
                      Filter
                    </span>
                  </v-btn>
                </div>

                <!-- ADD -->
                <div class="mr-3" v-if="config.action.add.button">
                  <v-btn v-if="!config.action.add.window"
                    elevation="0"
                    style="background: #4caf5033; border-radius: 10px;"
                    class="body-2 text-capitalize font-weight-bold d-flex align-center green--text"
                    @click="dialog.form = true; detail = {};">
                    <v-icon size="18">mdi-plus-circle-outline</v-icon>

                    <span class="ml-1">
                      Tambah
                    </span>
                  </v-btn>

                  <v-btn v-else
                    elevation="0"
                    style="background: #4caf5033; border-radius: 10px;"
                    class="body-2 text-capitalize font-weight-bold d-flex align-center green--text"
                    :to="config.action.add.href">
                    <v-icon size="18">mdi-plus-circle-outline</v-icon>

                    <span class="ml-1">
                      Tambah
                    </span>
                  </v-btn>
                </div>

                <div v-if="selected.row.length > 0 && !selected.row[0].is_kpi_bool">
                  <div v-if="
                            (selected.row.length === 1 && config.action.edit.button) ||
                            (selected.row.length > 0 && config.action.delete.button) || 
                            (selected.row.length === 1 && config.action.detail.button) ||
                            (selected.row.length === 1 && config.action.attachment.button) ||
                            (selected.row.length > 0 && config.action.active.button) ||
                            (selected.row.length > 0 && config.action.deactive.button) ||
                            (selected.row.length === 1 && config.action.approve.button && selected.status_approved) ||
                            (selected.row.length === 1 && config.action.reject.button && selected.status_rejected) ||
                            (selected.row.length > 0 && config.action.reset.button)">
                    <v-menu
                      bottom
                      offset-y
                      style="border-radius: 10px !important;">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          width="36"
                          min-width="36"
                          height="36"
                          outlined
                          elevation="0"
                          class="px-0"
                          style="border-radius: 10px;"
                          :color="set_color"
                          v-bind="attrs"
                          v-on="on">
                          <v-icon>mdi-cog-box</v-icon>
                        </v-btn>
                      </template>

                      <v-list
                        class="pa-2"
                        style="border-radius: 10px !important;">
                        <v-list-item
                          class="pa-0">
                          <!-- EDIT -->
                          <div class="mx-2" v-if="selected.row.length === 1 && config.action.edit.button">
                            <v-btn v-if="!config.action.edit.window"
                              elevation="0"
                              style="background: #2196f333; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center blue--text"
                              @click="dialog.form = true; detail = selected.row[0];">
                              <v-icon size="18">mdi-circle-edit-outline</v-icon>

                              <span class="ml-1">
                                {{ config.action.edit.text === undefined ? 'Ubah' : config.action.edit.text }}
                              </span>
                            </v-btn>

                            <v-btn v-else
                              elevation="0"
                              style="background: #2196f333; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center blue--text"
                              :to="`${config.action.edit.href}/${selected.row[0].id}`">
                              <v-icon size="18">mdi-circle-edit-outline</v-icon>

                              <span class="ml-1">
                                {{ config.action.edit.text === undefined ? 'Ubah' : config.action.edit.text }}
                              </span>
                            </v-btn>
                          </div>

                          <!-- DELETE -->
                          <div class="mx-2" v-if="selected.row.length > 0 && config.action.delete.button">
                            <v-btn
                              elevation="0"
                              style="background: #f4403433; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center red--text"
                              @click="toDelete()">
                              <v-icon size="18">mdi-delete-circle-outline</v-icon>

                              <span class="ml-1">
                                Hapus
                              </span>
                            </v-btn>
                          </div>

                          <!-- DETAIL -->
                          <div class="mx-2" v-if="selected.row.length === 1 && config.action.detail.button">
                            <v-btn
                              @click="toRead()"
                              elevation="0"
                              style="background: #e0e0e0; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center text-second">
                              <v-icon size="18">mdi-information-outline</v-icon>

                              <span class="ml-1">
                                {{ config.action.detail.text === undefined ? 'Detail' : config.action.detail.text }}
                              </span>
                            </v-btn>
                          </div>

                          <!-- ATTACHMENT -->
                          <div class="mx-2" v-if="selected.row.length === 1 && config.action.attachment.button">
                            <v-btn
                              @click="dialog.attachment = true"
                              elevation="0"
                              style="background: #e0e0e0; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center text-second">
                              <v-icon size="18">mdi-paperclip</v-icon>

                              <span class="ml-1">
                                Lampiran
                              </span>
                            </v-btn>
                          </div>

                          <!-- ACTIVE -->
                          <div class="mx-2" v-if="selected.row.length > 0 && config.action.active.button">
                            <v-btn
                              elevation="0"
                              style="background: #00bcd433; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center cyan--text"
                              @click="toActive()">
                              <v-icon size="18">mdi-check-circle-outline</v-icon>

                              <span class="ml-1">
                                Aktif
                              </span>
                            </v-btn>
                          </div>

                          <!-- DEACTIVE -->
                          <div class="mx-2" v-if="selected.row.length > 0 && config.action.deactive.button">
                            <v-btn
                              elevation="0"
                              style="background: #ff980033; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center orange--text"
                              @click="toDeactive()">
                              <v-icon size="18">mdi-swap-horizontal-circle-outline</v-icon>

                              <span class="ml-1">
                                Non Aktif
                              </span>
                            </v-btn>
                          </div>

                          <!-- APPROVE -->
                          <div class="mx-2" v-if="selected.row.length === 1 && config.action.approve.button && selected.status_approved">
                            <v-btn
                              elevation="0"
                              style="background: #004d4033; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center teal--text text-darken-4"
                              @click="toApprove()">
                              <v-icon size="18">mdi-information-outline</v-icon>

                              <span class="ml-1">
                                Approve
                              </span>
                            </v-btn>
                          </div>

                          <!-- REJECT -->
                          <div class="mx-2" v-if="selected.row.length === 1 && config.action.reject.button && selected.status_rejected">
                            <v-btn
                              elevation="0"
                              style="background: #880e4f33; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center pink--text text-darken-4"
                              @click="toReject()">
                              <v-icon size="18">mdi-information-outline</v-icon>

                              <span class="ml-1">
                                Reject
                              </span>
                            </v-btn>
                          </div>

                          <!-- RESET -->
                          <div class="mx-2" v-if="selected.row.length > 0 && config.action.reset.button">
                            <v-btn
                              elevation="0"
                              style="background: #ff980033; border-radius: 10px;"
                              class="body-2 text-capitalize font-weight-bold d-flex align-center orange--text"
                              @click="toReset()">
                              <v-icon size="18">mdi-lock-reset</v-icon>

                              <span class="ml-1">
                                Reset Password
                              </span>
                            </v-btn>
                          </div>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </div>

                <!-- <div v-if="selected.row.length === 1 && $route.path === '/officer'">
                  <v-btn
                    @click="dialog.form_user_doc = true; detail = selected.row[0]; fetchUserDoc(selected.row[0].id);"
                    elevation="0"
                    style="background: #e0e0e0; border-radius: 10px;"
                    class="body-2 text-capitalize font-weight-bold d-flex align-center text-second">
                    <v-icon size="18">mdi-information-outline</v-icon>

                    <span class="ml-1">
                      Dokumen
                    </span>
                  </v-btn>
                </div> -->
              </v-col>

              <v-col 
                cols="12" 
                md="4" 
                class="d-flex align-center justify-end">
                <v-text-field
                  filled
                  rounded
                  dense
                  single-line
                  hide-details
                  :color="set_color"
                  placeholder="Cari . . ."
                  vvvv-uppercase
                  append-icon="mdi-magnify"
                  clearable
                  v-model="search"
                  @click:clear="search = ''; fetch();"
                  v-on:keyup.enter="fetch()"
                  style="border-radius: 10px;">
                </v-text-field>

                <!-- PRINT -->
                <!-- <div class="ml-3" v-if="pagination.total_data > 0 && config.action.print.button">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        :color="set_color"
                        elevation="0"
                        min-width="36"
                        class="text-capitalize white--text px-0"
                        style="border-radius: 10px;"
                        :disabled="process.print || selected.row.length < 1"
                        :loading="process.print"
                        @click="prints()"
                        v-bind="attrs"
                        v-on="on">
                        <v-icon>mdi-microsoft-excel</v-icon>
                        <v-icon>mdi-file-pdf-box</v-icon>
                      </v-btn>
                    </template>
                    
                    <span>
                      Print
                    </span>
                  </v-tooltip>
                </div> -->

                <div class="ml-3" v-if="pagination.total_data > 0 && config.action.print.button">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        :color="set_color"
                        elevation="0"
                        min-width="36"
                        class="text-capitalize white--text px-0"
                        style="border-radius: 10px;"
                        :disabled="process.print"
                        :loading="process.print"
                        @click="prints()"
                        v-bind="attrs"
                        v-on="on">
                        <v-icon>mdi-microsoft-excel</v-icon>
                      </v-btn>
                    </template>
                    
                    <span>
                      Print
                    </span>
                  </v-tooltip>
                </div>

                <!-- FREEZE COLUMN -->
                <div class="ml-3" v-if="config.action.freeze.button">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        elevation="0"
                        outlined
                        dark
                        min-width="36"
                        :color="set_color"
                        class="text-capitalize px-0"
                        style="border-radius: 10px;"
                        v-bind="attrs"
                        v-on="on"
                        @click="dialog.freeze = true">
                        <v-icon size="18">mdi-table-split-cell</v-icon>
                      </v-btn>
                    </template>

                    <span>
                      Freeze
                    </span>
                  </v-tooltip>
                </div>

                <!-- HIDE COLUMN -->
                <div class="ml-3" v-if="config.action.hide.button">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        elevation="0"
                        outlined
                        dark
                        min-width="36"
                        color="#7B7B7B"
                        class="text-capitalize px-0"
                        style="border-radius: 10px;"
                        v-bind="attrs"
                        v-on="on"
                        @click="dialog.hide = true">
                        <v-icon size="18">mdi-eye-off-outline</v-icon>
                      </v-btn>
                    </template>

                    <span>
                      Hide
                    </span>
                  </v-tooltip>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-tabs 
            v-if="config.action.tab.button"
            v-model="selected.menu"
            flat
            slider-size="5"
            :color="set_color"
            class="mb-3">
            <v-tab
              class="d-flex justify-start"
              v-for="(item, index) in config.action.tab.api"
              :key="index"
              style="min-width: 70px;">
              <v-icon size="20" left>
                {{ item.icon }}
              </v-icon>
              
              <span 
                class="body-2 text-capitalize"
                :class="selected.menu === index ? 'font-weight-bold' : ''">
                {{ item.text }}
              </span>
            </v-tab>
          </v-tabs>

          <v-data-table
            v-model="selected.row"
            ref="table-grid"
            class="table-grid"
            :height="!config.action.tab.button && !config.height ? 
                  `calc(100vh - ${pagination.total_data > 0 ? '230px' : '158px'})` :
                  `calc(100vh - ${pagination.total_data > 0 ? '324px' : '252px'})`"
            :show-select="config.checkbox"
            :single-select="config.single"
            :loading="process.load"
            loading-text="Sedang memuat data . . ."
            loader-height="5"
            no-results-text="Belum ada data"
            :options.sync="options"
            :headers="configHeaders"
            :items="list"
            :items-per-page="100"
            item-key="id"
            @click:row="rowClick"
            @toggle-select-all="handleToggleAll($event)"
            @dblclick:row="doubleClick"
            hide-default-footer>
            <template
              v-for="header in configHeaders.filter((header) => header.hasOwnProperty('format'))"
              v-slot:[`item.${header.value}`]="{ value }">
              {{ rowFormat(header.format, value) }} 
            </template>

            <template v-slot:[`item.index`]="{ index }">
              {{ lastIndex + index }}
            </template>

            <v-progress-linear 
              v-show="process.load" 
              slot="progress" 
              color="white" 
              indeterminate>
            </v-progress-linear>
          </v-data-table>

          <pagination v-if="pagination.total_data > 0"
            @changePage="fetch($event)" 
            @reloadPage="fetch($event)" 
            :model="pagination"/>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import { get, post, put, destroy } from "@/service/Axios";
  import { TokenService } from "@/service/Storage.Service";
  import axios from "axios";
  import moment from "moment"
  import "moment/locale/id"
  export default {
    props: {
      config: {
        type: Object,
        default: {}
      }
    },
    data () {
      return {
        dialog: {
          fullscreen: false,
          snackbar: false,
          filter: false,
          freeze: false,
          form: false,
          delete: false,
          approve: false,
          reject: false,
          hide: false,
          active: false,
          deactive: false,

          form_user_doc: false,

          attachment: false,
          
          reset_password: false
        },
        search: '',
        filters: [],
        selected: {
          row: [],
          freeze: [],
          delete: [],
          approve: [],
          reject: [],
          active: [],
          deactive: [],

          // TAB
          menu: 0,

          // CUSTOM
          status_approved: false,
          status_rejected: false,

          reset_password: []
        },
        options: {},
        headers: [],
        detail: {},
        list: [],
        pagination: {
          total_data: 0
        },
        lastIndex: 0,
        limit: 20,
        form: { reason: '' },
        process: {
          load: false,
          delete: false,
          approve: false,
          reject: false,
          print: false,
          active: false,
          deactive: false,

          reset_password: false
        },
        message: {
          success: '',
          error: ''
        },

        form_static: {
          subdistrict: '',
          village: ''
        },
        autocomplete: {
          subdistrict: '',
          village: ''
        },
        static_loc: {
          subdistrict: [],
          village: []
        },

        // USER DOC
        form_user_doc: {
          user_id: '',
          ktp: '',
          kk: '',
          npwp: '',
          bpjs_kesehatan: '',
          bpjs_ketenagakerjaan: ''
        }
      }
    },
    components: {
      pagination: () => import(/* webpackPrefetch: true */ '@/components/pagination.vue'),
      forms: () => import(/* webpackPrefetch: true */ '@/components/basic/form.vue'),
      formdocs: () => import(/* webpackPrefetch: true */ '@/components/basic/form_doc.vue')
    },
    computed: {
      rps() {
        return this.$vuetify.breakpoint.name
      },
      configHeaders () {
        return this.headers.filter(header => !header.hide)  
      },
      set_color () {
        return this.$store.state.color
      }
    },
    watch: {
      'options': {
        handler () {
          let pagination = { row: this.limit, page: 1 }
          this.fetch(pagination)
        },
        deep: true
      },
      'selected.row': function(val) {
        if (val.length > 0) this.selected.status_approved = val[0].report_assessment_status === 'confirmed' ? true : false

        if (val.length > 0) this.selected.status_rejected = val[0].report_assessment_status === 'waiting' ? true : false
      },
      'selected.menu': function() {
        this.fetch()
      },
      'dialog.form': function() {
        if (Object.keys(this.detail).length > 0) {
          let field = this.detail, t = this
          
          Object.keys(field).forEach(function(key) {
            let field_key = t.config.prefix.length > 0 ? key.split(t.config.prefix)[1] : key

            t.config.form.map(obj => { 
              if (obj.type !== 'column') {
                if (obj.field === field_key) {
                  obj.value = field[key]
                }
              } else {
                obj.sub.map(obj_sub => {
                  if (obj_sub.field === field_key) {
                    obj_sub.value = field[key]
                  }
                })
              }
            })
          })
        } else {
          // this.config.form.map(obj => { obj.value === 'select' || obj.value === 'radio' ? obj.value : '' })

          this.config.form.map(obj => { 
            // if (obj.type !== 'id') obj.value = ''
            obj.value = ''
          })
        }
      },

      'form_static.subdistrict': function() {
        this.static_location().village()
      },
      'form_static.village': function() {
        
      },
      'autocomplete.subdistrict': function() {
        this.static_location().subdistrict()
      },
      'autocomplete.village': function() {
        this.static_location().village()
      }
    },
    mounted () {
      
    },
    methods: {
      rowFormat (format, value) {
        let data = value

        if (format === 'number') {
          if (isNaN(value)) return 'Not a Number'
          let price = ''

          const reverseNumber = value.split('.')[0]
            .toString()
            .split('')
            .reverse()
            .join('')
          const arrReverseNumber = [...Array(reverseNumber.length).keys()]

          arrReverseNumber.map(index => {
            if (index % 3 === 0) price += reverseNumber.substr(index, 3) + '.'
          })

          return `Rp ${
            price.split('', price.length - 1)
            .reverse()
            .join('')
          },00`
        } else if (format === 'array') {
          let arr = []

          for (let i = 0; i < value.length; i++) {
            arr.push(value[i].title)
          }

          data = arr.join(', ')
        } else if (format === 'date') {
          data = value.length > 0 ? moment(value).format('D MMM, YYYY') : '-'
        } else if (format === 'time') {
          data = value.length > 0 ? moment(value).format('HH:mm') : '-'
        } else if (format === 'datetime') {
          data = value.length > 0 ? moment(value).format('D MMM, YYYY HH:mm') : '-'
          // data = value.length > 0 ? moment(value).format(format === 'date' ? 'D MMM, YYYY' : format === 'time' ? 'HH:mm' : 'D MMM, YYYY HH:mm') : '-'
        } else if (format === 'object') {
          data = value.createdby.name
        } else if (format === 'regex') {
          let regex = /(<([^>]+)>)/ig

          data = value.replace(regex, '')
        } else if (format === 'on_off') {
          data = value ? 'Online' : 'Offline'
        } else if (format === 'g_m') {
          data = value ? 'Mandiri' : 'Group'
        } else if (format === 'v_a') {
          data = value ? 'Sudah Verifikasi' : 'Belum Verifikasi'
        } else if (format === 'y_n') {
          data = parseInt(value) ? 'Ya' : 'Tidak'
        } else if (format === 'kpi') {
          data = value ? 'Untuk KPI' : 'Umum'
        } else if (format === 'role') {
          data = value === 'ga' ? 'general affair' : value
        } else {
          data = value ? 'Aktif' : 'Tidak Aktif'
        }
        return data
      },
      rowClick (item, row) {
        row.select(!row.isSelected ? true : false)
      },
      doubleClick (event, {item}) {
        // this.dialog.snackbar = true
        // this.message.success = item
      },
      handleToggleAll (event) {
        if (!event.value) this.selected.row = []
      },
      handleFilters () {
        let data = this.config.filter,
            arr = []

        for (let i = 0; i < data.length; i++) {
          if (data[i].sub === undefined) {
            arr.push({
              type: 'string',
              field: data[i].field,
              value: data[i].value,
              comparison: '<>'
            })
          } else {
            let sub = data[i].sub

            for (let s = 0; s < sub.length; s++) {
              arr.push({
                type: 'string',
                field: sub[s].field,
                value: sub[s].value,
                comparison: '<>'
              })
            }
          }
        }

        if (this.config.action.tab.button && this.$route.path !== '/officer') {
          arr.push({
            type: 'string',
            field: this.config.action.tab.field,
            value: this.config.action.tab.api[this.selected.menu].value !== 'All' ? this.config.action.tab.api[this.selected.menu].value : '',
            comparison: '<>'
          })
        }

        return arr
      },
      async fetch (pagination = { row: 20, page: 1 }) {
        this.process.load = true

        this.limit = pagination.row

        // let default_sort = this.$route.path === '/officer' ? 'username' : 'id'
        let default_sort = this.$route.path === '/officer' ? '-id' : 'id'

        let params = {
          limit: pagination.row,
          page: pagination.page,
          sort: this.options.sortBy.length < 1 ? default_sort : this.options.sortBy[0],
          order_by: this.options.sortDesc[0] ? 'ASC' : 'DESC',
          // order_by: this.options.sortDesc[0] ? 'DESC' : 'ASC',
          search: this.search,
          filter: this.handleFilters(),

          is_active: this.$route.path === '/officer' ? this.config.action.tab.api[this.selected.menu].value : ''
        }

        this.selected.row = []

        await get(this.config.api, { params }).then(response => {
          let res = response.data

          this.process.load = false

          if (res.status === 200) {
            this.filters = this.config.filter
            this.headers = this.config.header
            
            this.list = res.results.data
            
            this.pagination = res.results.pagination !== undefined ? res.results.pagination : { 
              total_data: res.results.data.length,
              total_page: 1,
              total_display: res.results.data.length,
              first_page: false,
              last_page: false,
              prev: 0,
              current: 1,
              next: 0,
              detail: [],
              start: 1,
              end: res.results.data.length
            }

            this.lastIndex = (pagination.page - 1) * pagination.row 
            this.lastIndex += 1

            this.addFreezes()

            this.$vuetify.goTo(this.$refs['table-grid'], { container: this.$refs['table-grid'].$el.querySelector('div.v-data-table__wrapper') })

            if (this.headers.length < 7 && this.list.length > 0) this.getTableToResize()
          }
        })
      },
      toDelete () {
        this.selected.delete = []

        for (let i in this.selected.row) {
          this.selected.delete.push(parseInt(this.selected.row[i].id))
        }

        this.dialog.delete = true
      },
      async deletes () {
        this.message.error = ''

        this.process.delete = true

        await post(`${this.config.action.delete.api}`, {
          data: {
            id: this.selected.delete[0]
          }
        }).then(response => {
          let res = response.data

          if (res.status === 200) {
            this.process.delete = false
            this.dialog.delete = false

            this.selected.row = []
            this.selected.delete = []
            
            this.dialog.snackbar = true
            this.message.success = res.message

            this.dialog.fullscreen = false

            this.fetch()
          } else {
            this.process.delete = false
            this.message.error = res.message
          }
        })
      },
      toActive () {
        this.selected.active = []

        for (let i in this.selected.row) {
          this.selected.active.push(parseInt(this.selected.row[i].id))
        }

        this.dialog.active = true
      },
      async actived () {
        this.message.error = ''

        this.process.active = true

        await post(`${this.config.action.active.api}`, {
          data: {
            id: this.selected.active[0]
          }
        }).then(response => {
          let res = response.data

          if (res.status === 200) {
            this.process.active = false
            this.dialog.active = false

            this.selected.row = []
            this.selected.active = []
            
            this.dialog.snackbar = true
            this.message.success = res.message

            this.dialog.fullscreen = false

            this.fetch()
          } else {
            this.process.active = false
            this.message.error = res.message
          }
        })
      },
      toDeactive () {
        this.selected.deactive = []

        for (let i in this.selected.row) {
          this.selected.deactive.push(parseInt(this.selected.row[i].id))
        }

        this.dialog.deactive = true
      },
      async deactived () {
        this.message.error = ''

        this.process.deactive = true

        await post(`${this.config.action.deactive.api}`, {
          data: {
            id: this.selected.deactive[0]
          }
        }).then(response => {
          let res = response.data

          if (res.status === 200) {
            this.process.deactive = false
            this.dialog.deactive = false

            this.selected.row = []
            this.selected.deactive = []
            
            this.dialog.snackbar = true
            this.message.success = res.message

            this.dialog.fullscreen = false

            this.fetch()
          } else {
            this.process.deactive = false
            this.message.error = res.message
          }
        })
      },
      toApprove () {
        this.selected.approve = []

        for (let i in this.selected.row) {
          this.selected.approve.push(parseInt(this.selected.row[i].id))
        }

        this.dialog.approve = true
      },
      async approved () {
        this.message.error = ''

        this.process.approve = true

        await post(`${this.config.action.approve.api}`, {
          data: {
            id: this.selected.approve[0]
          }
        }).then(response => {
          let res = response.data

          if (res.status === 200) {
            this.process.approve = false
            this.dialog.approve = false

            this.selected.row = []
            this.selected.approve = []
            
            this.dialog.snackbar = true
            this.message.success = res.message

            this.dialog.fullscreen = false

            this.fetch()
          } else {
            this.process.approve = false
            this.message.error = res.message
          }
        })
      },
      toReject () {
        this.selected.reject = []

        for (let i in this.selected.row) {
          this.selected.reject.push(parseInt(this.selected.row[i].id))
        }

        this.dialog.reject = true
      },
      async rejected () {
        this.message.error = ''

        this.process.reject = true

        await post(`${this.config.action.reject.api}`, {
          data: {
            id: this.selected.reject[0],
            reason: this.form.reason
          }
        }).then(response => {
          let res = response.data
          if (res.status === 200) {
            this.process.reject = false
            this.dialog.reject = false

            this.selected.row = []
            this.selected.reject = []

            this.form.reason = ''
            
            this.dialog.snackbar = true
            this.message.success = res.message

            this.dialog.fullscreen = false

            this.fetch()
          } else {
            this.process.reject = false
            this.message.error = res.message
          }
        })
      },
      async prints () {
        this.message.error = ''

        this.process.print = true

        // let params = {
        //   id: this.selected.row[0].id
        // }

        // await get(`${this.config.action.print.api}`, { params }).then(response => {
        //   let res = response.data

        //   if (res.status === 200) {
        //     this.process.print = false

        //     window.open(res.data.url, '_blank')
        //   } else {
        //     this.process.print = false
        //     this.message.error = res.message
        //   }
        // })

        await axios({
          url: `${process.env.VUE_APP_API_URL}${this.config.action.print.api}`,
          method: 'GET',
          responseType: 'blob', // important,
          // params: params,
          headers: {
            Authorization: process.env.VUE_APP_XAUTH,
            token: TokenService.getToken()
          }
        }).then((response) => {
          let res = response

          if (res.status === 200) {
            this.process.print = false
            
            this.dialog.snackbar = true
            this.message.success = 'Berhasil mengeksport excel'

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `Amanata-${moment().format('DD-MMMM-YYYY', 'id')}-${moment().format('HH:mm:ss', 'id')}.xls`)
            document.body.appendChild(link)
            link.click()
          } else {
            this.process.print = false
            this.message.error = res.message
          }
        })
      },
      addFreezes () {
        this.removeFreezes()

        this.selected.freeze = []

        for (let i = 0; i < this.configHeaders.length; i++) {
          if (this.configHeaders[i].freeze) {
            // ADD TO SELECTOR
            this.selected.freeze.push({
              child: i + (this.config.checkbox ? 2 : 1),
              width: this.configHeaders[i].width.split('px')[0]
            })

            // LOOP STYLESHEET
            for (let f = 0; f < this.selected.freeze.length; f++) {
              let style = document.createElement('style'),
                  width = 0

              if (this.config.checkbox) {
                width = f < 1 ? '65' : 
                      f === 1 ? parseInt(this.selected.freeze[f - 1].width) + 65 : 
                      parseInt(this.selected.freeze[f - 2].width) + parseInt(this.selected.freeze[f - 1].width) + 65
              } else {
                width = f < 1 ? '0' : 
                      f === 1 ? parseInt(this.selected.freeze[f - 1].width) : 
                      parseInt(this.selected.freeze[f - 2].width) + parseInt(this.selected.freeze[f - 1].width)
              }

              // ADD STYLESHET TO FREEZE
              style.innerHTML = `
                .table-grid table > thead > tr > th:nth-child(${this.selected.freeze[f].child}) {
                  left: ${width}px;
                  z-index: 5;
                  background: ${this.set_color};
                }

                .table-grid table > tbody > tr > td:nth-child(${this.selected.freeze[f].child}) {
                  left: ${width}px;
                  z-index: 1;
                }
              `

              // ADD TO APPENDCHILD
              document.head.appendChild(style)
            }
          }
        }
      },
      removeFreezes () {
        // LOOP STYLESHEET
        for (let f = 0; f < this.selected.freeze.length; f++) {
          let style = document.createElement('style')
          
          // RESET STYLESHEET TO AUTO
          style.innerHTML = `
            .table-grid table > thead > tr > th:nth-child(${this.selected.freeze[f].child}) {
              left: auto;
              z-index: auto;
              background: ${this.set_color};
            }

            .table-grid table > tbody > tr > td:nth-child(${this.selected.freeze[f].child}) {
              left: auto;
              z-index: auto;
            }
          `

          // ADD TO APPENDCHILD
          document.head.appendChild(style)
        }
      },
      addHides () {
        if (this.headers.length < 6) this.getTableToResize()
      },
      getTableToResize () {
        let tables = document.getElementsByTagName('table')

        for (let i = 0; i < tables.length; i++) {
          this.resizableGrid(tables[i])
        }
      },
      resizableGrid (table) {
        let row = table.getElementsByTagName('tr')[0],
            cols = row ? row.children : undefined
        
        if (!cols) return
        
        table.style.overflow = 'hidden'
        
        let tableHeight = table.offsetHeight
        
        for (let i = 0; i < cols.length; i++) {
          let div = createDiv(tableHeight)
          cols[i].appendChild(div)
          cols[i].style.position = 'relative'

          setListeners(div)
        }

        function setListeners (div) {
          let pageX, curCol, nxtCol, curColWidth, nxtColWidth

          div.addEventListener('mousedown', e => {
            curCol = e.target.parentElement
            nxtCol = curCol.nextElementSibling
            pageX = e.pageX
            
            let padding = paddingDiff(curCol)
            
            curColWidth = curCol.offsetWidth - padding

            if (nxtCol) nxtColWidth = nxtCol.offsetWidth - padding
          })

          div.addEventListener ('mouseover', e => {
            e.target.style.borderRight = '2px solid #e0e0e0'
          })

          div.addEventListener ('mouseout', e => {
            e.target.style.borderRight = ''
          })

          document.addEventListener ('mousemove', e => {
            if (curCol) {
              let diffX = e.pageX - pageX
          
              if (nxtCol) nxtCol.style.width = (nxtColWidth - (diffX))+'px'

              curCol.style.width = (curColWidth + diffX)+'px'
            }
          })

          document.addEventListener ('mouseup', () => { 
            curCol = undefined
            nxtCol = undefined
            pageX = undefined
            nxtColWidth = undefined
            curColWidth = undefined
          })
        }

        function createDiv (height) {
          let div = document.createElement('div')

          div.style.top = 0
          div.style.right = 0
          div.style.width = '1px'
          div.style.position = 'absolute'
          div.style.cursor = 'col-resize'
          div.style.userSelect = 'none'
          div.style.height = height + 'px'
          div.style.background = '#e0e0e0'

          return div
        }

        function paddingDiff (col) {
          if (getStyleVal(col,'box-sizing') == 'border-box') {
            return 0
          }
          
          let padLeft = getStyleVal(col,'padding-left')
          let padRight = getStyleVal(col,'padding-right')

          return (parseInt(padLeft) + parseInt(padRight))
        }

        function getStyleVal (elm,css) {
          return (window.getComputedStyle(elm, null).getPropertyValue(css))
        }
      },
      async toRead () {
        if (this.$route.name === 'Pegawai' || this.$route.name === 'Event') {
          this.$router.push(`${this.config.action.detail.api}${this.selected.row[0].id}`)
        } else {
          this.$router.push(`${this.config.action.detail.api}${this.selected.row[0].user_id}`)
        }
      },

      static_location () {
        let t = this
        return {
          async subdistrict () {
            let params = {
              search: t.autocomplete.subdistrict,
              sort: 'DESC'
            }

            await get('location/kecamatan', { params })
            .then(response => {
              let res = response.data
            
              if (res.status === 200) {
                t.static_loc.subdistrict = res.data
              }
            })
          },
          async village () {
            let params = {
              desa_kecamatan_code: t.form_static.subdistrict,
              search: t.autocomplete.village,
              sort: 'DESC'
            }

            await get('location/desa', { params })
            .then(response => {
              let res = response.data
            
              if (res.status === 200) {
                t.static_loc.village = res.data
              }
            })
          }
        }
      },


      // USER DOC
      async fetchUserDoc (id) {
        let params = {
          user_id: id
        }

        await get('admin/user/doc', { params }).then(response => {
          let res = response.data

          if (res.status === 200) {
            this.form_user_doc = {
              user_id: this.detail.id,
              ktp: res.results.data.ktp,
              kk: res.results.data.kk,
              npwp: res.results.data.npwp,
              bpjs_kesehatan: res.results.data.bpjs_kesehatan,
              bpjs_ketenagakerjaan: res.results.data.bpjs_ketenagakerjaan
            }
          } else {
            this.form_user_doc = {
              user_id: this.detail.id,
              ktp: '',
              kk: '',
              npwp: '',
              bpjs_kesehatan: '',
              bpjs_ketenagakerjaan: ''
            }
          }
        })
      },

      toReset () {
        this.selected.reset_password = []

        for (let i in this.selected.row) {
          this.selected.reset_password.push(parseInt(this.selected.row[i].id))
        }

        this.dialog.reset_password = true
      },
      async resetPassword () {
        this.message.error = ''

        this.process.reset_password = true

        await post(`${this.config.action.reset.api}`, {
          data: {
            id: this.selected.reset_password[0]
          }
        }).then(response => {
          let res = response.data

          if (res.status === 200) {
            this.process.reset_password = false
            this.dialog.reset_password = false

            this.selected.row = []
            this.selected.reset_password = []
            
            this.dialog.snackbar = true
            this.message.success = res.message

            this.dialog.fullscreen = false

            this.fetch()
          } else {
            this.process.reset_password = false
            this.message.error = res.message
          }
        })
      }
    }
  }
</script>

<style>
  .table-grid table thead > tr > th, .table-grid table tbody > tr > td {
    position: sticky;
    position: -webkit-sticky;
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
  }

  .table-grid table > tbody > tr > td:nth-child(1) {
    left: 0; 
    z-index: 1;
  }

  .table-grid table > tbody > tr:nth-of-type(even) .text-start, table > tbody > tr:nth-of-type(even) .text-center, table > tbody > tr:nth-of-type(even) .text-end {
    background: #fafafa;
  }

  .table-grid table > tbody > tr:nth-of-type(odd) .text-start, table > tbody > tr:nth-of-type(odd) .text-center, table > tbody > tr:nth-of-type(odd) .text-end {
    background: white;
  }
</style>